import React, { useState } from 'react'

import 'chart.js/auto'
import type { ChartEvent, ChartType, ScriptableContext } from 'chart.js'
import { Bubble } from 'react-chartjs-2'
import ChartDataLabels from 'chartjs-plugin-datalabels'

import { OptionProps } from 'components_new/molecules/OptionsMenu'

import LoadingSection from 'components_new/molecules/LoadingSection'

import { getTheme } from 'themes'

import ChartOptionsMenu from '../ChartOptionsMenu'
import ChartWrapper from '../ChartWrapper'

import { TChartClick, TChartData, TChartVariant } from '../types'
import { EMPTY_CHART_CLICK } from '../utils/constants'
import {
  getChartBackgroundColorOptions,
  getChartBorderColorOptions,
  getChartTooltipOptions
} from '../utils/options'
import { drawHorizontalLines } from '../plugins/drawHorizontalLines'
import { drawVerticalLines } from '../plugins/drawVerticalLines'
import { indicateClickOnBubbleHover } from '../plugins/indicateClickOnBubbleHover'
import { recalculateBubbleRadiusOnResize } from '../plugins/recalculateBubbleRadiusOnResize'
import {
  getBubbleChartScalesOptions,
  getBubblePoints,
  getBubbleRadius,
  getBubbleRows,
  getRDomain,
  handleOnSegmentClick,
  TBubblePoint
} from './utils'

interface BubbleChartProps {
  data: TChartData // 1st dataset = x, 2nd dataset = y, 3rd dataset = r
  decimals?: number
  flexItem?: boolean
  loading?: boolean
  onSegmentClick?: (segmentIndex: number | null) => void
  options?: OptionProps[] | ((payload: TChartClick) => OptionProps[])
  // Which bubbles get a datalabel; all of them when omitted.
  showLabel?: (point: TBubblePoint, index: number) => boolean
  significantFigures?: number
  variant: TChartVariant
}

const BubbleChart = (props: BubbleChartProps) => {
  const {
    data,
    decimals,
    flexItem,
    loading,
    onSegmentClick,
    options: menuOptions,
    showLabel,
    significantFigures,
    variant
  } = props

  const [clicked, setClicked] = useState<TChartClick>(EMPTY_CHART_CLICK)
  const theme = getTheme('light')

  const open =
    Boolean(clicked?.position?.left !== 0) ||
    Boolean(clicked?.position?.top !== 0)

  const handleClose = () => {
    setClicked((prevState) => ({
      ...prevState,
      position: {
        left: 0,
        top: 0
      }
    }))
  }

  const points = getBubblePoints(data)
  const { rMax, rMin } = getRDomain(points)

  return (
    <ChartWrapper flexItem={Boolean(flexItem)}>
      {loading ? (
        <LoadingSection background={true} />
      ) : (
        <>
          <Bubble
            data={{
              datasets: points.map((point) => ({
                backgroundColor: (ctx: ScriptableContext<ChartType>) =>
                  getChartBackgroundColorOptions({
                    ctx,
                    dataset: data.datasets[0]
                  }),
                borderColor: (ctx: ScriptableContext<ChartType>) =>
                  getChartBorderColorOptions({
                    ctx,
                    dataset: data.datasets[0]
                  }),
                data: [{ x: point.x, y: point.y, v: point.r }],
                hoverRadius: 5,
                label: point.label,
                radius: (ctx: ScriptableContext<'bubble'>) =>
                  getBubbleRadius({ ctx, rMax, rMin, value: point.r })
              }))
            }}
            options={{
              layout: {
                autoPadding: true
              },
              maintainAspectRatio: false,
              onClick: (event: ChartEvent, _active, chart) =>
                handleOnSegmentClick({
                  callback: (params) => {
                    if (onSegmentClick) {
                      onSegmentClick(params.datasetIndex ?? null)
                    }
                    setClicked(params)
                  },
                  chart,
                  event,
                  points
                }),
              onResize: (chart) => chart.update('none'),
              plugins: {
                datalabels: {
                  anchor: 'end',
                  align: 'end',
                  color: '#000000',
                  display: (ctx) => {
                    const point = points[ctx.datasetIndex]

                    if (!point) return false

                    return showLabel ? showLabel(point, ctx.datasetIndex) : true
                  },
                  // font: {
                  //   weight: 'bold'
                  // },
                  formatter: function (_, ctx) {
                    return ctx.dataset.label
                  },
                  offset: 2,
                  padding: 0
                },
                drawHorizontalLines: {
                  decimals,
                  lines: (data.horizontalLines ?? []).map((line) => ({
                    color: line.color ?? theme.palette.action.active,
                    label: line.label,
                    value: line.value
                  })),
                  significantFigures
                },
                drawVerticalLines: {
                  decimals,
                  lines: (data.verticalLines ?? []).map((line) => ({
                    color: line.color ?? theme.palette.action.active,
                    label: line.label,
                    value: line.value
                  })),
                  significantFigures
                },
                legend: {
                  display: false
                },
                tooltip: {
                  ...getChartTooltipOptions({
                    decimals,
                    getRows: (dataPoints) => {
                      const point = points[dataPoints[0]?.datasetIndex ?? -1]

                      if (!point) return []

                      return getBubbleRows({
                        data,
                        decimals,
                        point,
                        significantFigures
                      }).map((row) => ({
                        color: '#000', // $TODO
                        label: row.label,
                        type: 'bubble' as const,
                        value: row.value
                      }))
                    },
                    significantFigures,
                    unit: null,
                    variant
                  }),
                  callbacks: {
                    title: (items) =>
                      points[items[0]?.datasetIndex ?? -1]?.label ?? ''
                  }
                }
              },
              responsive: true,
              scales: getBubbleChartScalesOptions({ data, variant })
            }}
            plugins={[
              ChartDataLabels,
              drawHorizontalLines,
              drawVerticalLines,
              recalculateBubbleRadiusOnResize,
              ...(variant === 'detailed' ? [indicateClickOnBubbleHover] : [])
            ]}
          />

          {menuOptions ? (
            <ChartOptionsMenu
              clicked={clicked}
              onClose={handleClose}
              open={open}
              options={menuOptions}
              title={clicked.label}
            />
          ) : null}
        </>
      )}
    </ChartWrapper>
  )
}

export default BubbleChart
